/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Global } from '@emotion/react'
import { CalendarOutlined, ClockCircleOutlined } from '@ant-design/icons'
import {
  DatePicker as AntDatePicker,
  Switch,
  TimePicker as AntTimePicker,
  Tooltip,
} from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import timezonePlugin from 'dayjs/plugin/timezone'
import utcPlugin from 'dayjs/plugin/utc'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import permanentDstPlugin from '@sportninja/common/ts-utils/dayjs-permanent-dst'
import {
  getBrowserTimezone,
  getTimezoneAbbreviation,
} from '@sportninja/common/ts-utils/timezone-display'
import type {
  GameFormMode,
  GameFormValues,
} from '@sportninja/common/features/game/gameForm'
import {
  paragraphSmall,
  paragraphXSmall,
  weightRegular,
  weightSemiBold,
} from 'src/components/css'

// Same picker recipe as the registration setup general form
// (`RegistrationWindowFields`), so date/time entry behaves identically here.
//
// `type: 'mask'` gives a guided input: the placeholder stays in place and the
// caret steps through the segments as the admin types.
const DATE_MASK = { format: 'MM/DD/YYYY', type: 'mask' } as const

// Deliberately NOT masked. rc-picker's MaskFormat only treats
// ['YYYY','MM','DD','HH','mm','ss','SSS'] as editable cells, so under a mask a
// 12-hour `hh:mm A` exposes only the minutes -- the hours and the AM/PM marker
// are not cells and cannot be typed.
const TIME_FORMAT = 'hh:mm A'

// `.utc()` is used to read the TBD sentinel's true (UTC) date, `.tz()` to
// anchor the value on edit. Registered here rather than relying on another
// module in the bundle having done it first.
dayjs.extend(utcPlugin)
dayjs.extend(timezonePlugin)
// Keeps a zone that abolished its clock change reading on the offset it
// actually keeps, on a runtime whose database still has the old rule.
dayjs.extend(permanentDstPlugin)

const TIME_POPUP_CLASS = 'game-modal-time-popup'

const pad = (n: number): string => String(n).padStart(2, '0')

/**
 * A typed wall clock turned back into an instant, in a named zone.
 *
 * Composed from the parts rather than set onto an existing value: a `.tz()`
 * value FREEZES the offset it was built with, so moving a date across a DST
 * boundary would keep the old one and store an hour nobody typed.
 * `dayjs.tz(civil, zone)` resolves the offset for THAT date, and is the form
 * the permanent-DST plugin corrects. See ts-utils/dayjs-permanent-dst.
 */
const wallClockToIso = (
  zone: string,
  parts: {
    year: number
    month: number
    date: number
    hour: number
    minute: number
  }
): string =>
  dayjs
    .tz(
      `${parts.year}-${pad(parts.month + 1)}-${pad(parts.date)} ${pad(
        parts.hour
      )}:${pad(parts.minute)}:00`,
      zone
    )
    .toISOString()

interface Props {
  /**
   * EDIT ONLY anchors the picker to a named zone.
   *
   * The two modes publish the picked instant differently, so they need
   * different things from the picker. Edit sends it as-is, which makes the
   * picker the only place its zone is decided — and a bare `dayjs(iso)` decides
   * it on the runtime's timezone database, which the permanent-DST plugin never
   * sees, so a BC admin reads a 9:15 PM game as 8:15 PM and saves that back.
   *
   * Create instead re-reads the instant through `wallClockInZone` to recover
   * the typed reading, and that recovery goes through the runtime's own zone
   * too. The two uncorrected conversions cancel, so create is already right —
   * handing it a CORRECTED instant is what would break it, by an hour, on every
   * game created. Verified: it stored 03:45Z for a 9:45 PM pick.
   *
   * So the correction stops at the boundary where it is needed. Both modes end
   * up with the same USER-VISIBLE contract either way — the clock you type is
   * the venue's — which is what the abbreviation beside the label promises.
   * Unifying the two publish paths is the real fix and a larger change.
   */
  mode: GameFormMode
  values: GameFormValues
  onChange: (next: Partial<GameFormValues>) => void
  disabled?: boolean
  hasError?: (name: keyof GameFormValues) => boolean
  /** API error messages, keyed by form field name. */
  fieldErrors?: Record<string, string>
}

/**
 * Start date, start time and duration on one row, with the Time TBD switch
 * riding the Start Time label.
 *
 * TBD keeps the legacy semantics exactly: form state always holds the REAL
 * picked time, and the 23:58:58 UTC sentinel is applied on the way out in
 * `buildGamePayload`. That is what lets the toggle be turned back off without
 * having lost the time the admin chose. While it is on, the time field stays
 * visible but disabled rather than hiding, so the row never reflows.
 */
const GameDateTimeFields: React.FC<Props> = ({
  mode,
  values,
  onChange,
  disabled = false,
  hasError,
  fieldErrors = {},
}) => {
  /*
   * The zone the typed clock MEANS: the venue's, as it is on mobile, falling
   * back to the viewer's own when the venue has none (or none is chosen yet).
   *
   * The fallback is not a guess — it is what the publish path already does. A
   * game with no `locationTimezone` skips the create re-zone entirely and its
   * start is read on the runtime's own clock, so the viewer's zone IS the one
   * the typed reading lands in.
   */
  const zone = values.locationTimezone || getBrowserTimezone()

  /*
   * EDIT anchors the picker to that zone; create must not — see the `mode` prop.
   *
   * Empty only on a runtime that cannot name its own zone, where the
   * uncorrected path stands rather than throwing in `.tz('')`.
   */
  const anchored = mode === 'edit' && zone !== ''

  const parsedStart = values.startsAt ? dayjs(values.startsAt) : null
  const current =
    anchored && parsedStart?.isValid() ? parsedStart.tz(zone) : parsedStart

  /*
   * Labelled for the game's own instant, not for today: an abbreviation is a
   * property of a moment, so a November game edited in September has to say
   * PDT, not whatever the zone happens to be right now.
   *
   * Through the shared helper so a zone that abolished its clock change gets
   * the label its residents use. See ts-utils/timezone-display.
   */
  const zoneAbbreviation = zone
    ? getTimezoneAbbreviation(zone, (current ?? dayjs()).toDate())
    : ''

  const handleDate = (next: Dayjs | null) => {
    if (!next) {
      onChange({ startsAt: '' })
      return
    }
    // Keep the already-picked time-of-day when only the date changes; on the
    // first pick default to the current time rather than midnight, which is
    // almost never the intended kickoff.
    const source = current ?? dayjs()

    if (anchored) {
      //  Recomposed in the anchor zone rather than merged onto `next`, which
      //  antd hands back unanchored -- building on it would drop `source`'s
      //  hour back into the runtime's own offset and save it.
      onChange({
        startsAt: wallClockToIso(zone, {
          year: next.year(),
          month: next.month(),
          date: next.date(),
          hour: source.hour(),
          minute: source.minute(),
        }),
      })
      return
    }

    const merged = next
      .hour(source.hour())
      .minute(source.minute())
      .second(0)
      .millisecond(0)
    onChange({ startsAt: merged.toISOString() })
  }

  const handleTime = (next: Dayjs | null) => {
    if (!next) {
      return
    }
    // The field is disabled until a date exists, so `current` is normally set;
    // falling back to today keeps a stray change from being dropped silently.
    const base = current ?? dayjs()

    if (anchored) {
      onChange({
        startsAt: wallClockToIso(zone, {
          year: base.year(),
          month: base.month(),
          date: base.date(),
          hour: next.hour(),
          minute: next.minute(),
        }),
      })
      return
    }

    onChange({
      startsAt: base
        .hour(next.hour())
        .minute(next.minute())
        .second(0)
        .millisecond(0)
        .toISOString(),
    })
  }

  const handleDuration = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value
    onChange({ durationMinutes: raw === '' ? null : Number(raw) })
  }

  /**
   * Toggling TBD OFF pre-selects the current time of day, keeping the chosen
   * date.
   *
   * A game saved as TBD has no real time to restore -- its stored start is the
   * 23:58:58 UTC sentinel. Leaving that in place meant switching the toggle off
   * and pressing Confirm re-sent the sentinel, so the game stayed TBD unless
   * the user also changed the time by hand. Moving to "now" both gives a
   * sensible default and takes the value off the sentinel, so it stops reading
   * as TBD. Mirrors the legacy `handleTimeTDBChange`.
   */
  const handleTbdChange = (checked: boolean) => {
    if (checked) {
      onChange({ timeToBeDefined: true })
      return
    }

    const now = anchored ? dayjs().tz(zone) : dayjs()

    /*
     * Take the DAY from the sentinel in UTC, not local time.
     *
     * `setTimeToBeDefined` stamps 23:58:58 *UTC*, so in any zone ahead of UTC
     * that instant lands on the following local day (23:58 UTC on Apr 2 is
     * 08:58 on Apr 3 in Tokyo). Reading the date locally would therefore push
     * the game forward a day the moment TBD is switched off.
     */
    const day = current ? current.utc() : now

    if (anchored) {
      onChange({
        timeToBeDefined: false,
        startsAt: wallClockToIso(zone, {
          year: day.year(),
          month: day.month(),
          date: day.date(),
          hour: now.hour(),
          minute: now.minute(),
        }),
      })
      return
    }

    const restored = dayjs()
      .year(day.year())
      .month(day.month())
      .date(day.date())
      .hour(now.hour())
      .minute(now.minute())
      .second(0)
      .millisecond(0)

    onChange({ timeToBeDefined: false, startsAt: restored.toISOString() })
  }

  const popupContainer = (triggerNode: HTMLElement) =>
    triggerNode.parentNode as HTMLElement

  const startError = fieldErrors.startsAt
  const durationError = fieldErrors.durationMinutes

  return (
    <div css={groupStyle}>
      <div css={rowStyle}>
        {/* antd renders the time columns in a portal, so the selected cell is
            styled through a popup class rather than the trigger's css prop. */}
        <Global styles={timePopupStyles} />

        <div css={dateFieldStyle}>
          <span css={labelStyle}>
            {capitalizeWords('Start Date')}{' '}
            <span css={requiredMarkStyle}>*</span>
          </span>
          <AntDatePicker
            value={current}
            onChange={handleDate}
            format={DATE_MASK}
            disabled={disabled}
            status={hasError?.('startsAt') ? 'error' : ''}
            placeholder='MM/DD/YYYY'
            css={controlStyle}
            suffixIcon={<CalendarOutlined css={iconStyle} />}
            getPopupContainer={popupContainer}
          />
        </div>

        <div css={timeFieldStyle}>
          <span css={[labelStyle, splitLabelStyle]}>
            {/* The zone rides the label text itself -- it qualifies what the
                field MEANS, so it reads as part of the name rather than as a
                badge beside it. Inside the left group of the space-between
                label, so the TBD switch keeps the right edge to itself. */}
            <span>
              {capitalizeWords('Start Time')}
              {zoneAbbreviation ? ` (${zoneAbbreviation})` : ''}{' '}
              <span css={requiredMarkStyle}>*</span>
            </span>
            <Tooltip title='Time To Be Defined' zIndex={9999}>
              <span css={tbdStyle}>
                <span
                  css={[tbdTextStyle, values.timeToBeDefined && tbdTextOnStyle]}
                >
                  TBD
                </span>
                <Switch
                  size='small'
                  checked={values.timeToBeDefined}
                  disabled={disabled}
                  onChange={handleTbdChange}
                  aria-label='Time to be defined'
                />
              </span>
            </Tooltip>
          </span>
          <AntTimePicker
            value={values.timeToBeDefined ? null : current}
            onChange={handleTime}
            format={TIME_FORMAT}
            use12Hours
            needConfirm={false}
            /*
             * Quarter-hour options only -- games are scheduled on :00/:15/:30/:45
             * far more often than not, and a 60-row minute column makes the
             * common case a scroll.
             *
             * This limits the LIST, not the value: the input stays free text, so
             * an odd time is still typed straight in ("9:42 AM") and an existing
             * off-step time round-trips untouched.
             */
            minuteStep={15}
            // Disabled while TBD is on: the published time is the sentinel, so
            // letting it be edited would imply a precision that is not stored.
            disabled={disabled || values.timeToBeDefined || !values.startsAt}
            status={hasError?.('startsAt') ? 'error' : ''}
            placeholder={values.timeToBeDefined ? 'TBD' : 'hh:mm AM'}
            css={controlStyle}
            suffixIcon={<ClockCircleOutlined css={iconStyle} />}
            popupClassName={TIME_POPUP_CLASS}
            getPopupContainer={popupContainer}
          />
        </div>

        <div css={durationFieldStyle}>
          <span css={labelStyle}>
            {capitalizeWords(t('common:duration', {}))}{' '}
            <span css={requiredMarkStyle}>*</span>
          </span>
          <div
            css={[
              durationWrapperStyle,
              hasError?.('durationMinutes') && durationErrorStyle,
              disabled && durationDisabledStyle,
            ]}
          >
            <input
              type='number'
              min={0}
              step={1}
              value={values.durationMinutes ?? ''}
              placeholder='e.g. 60'
              onChange={handleDuration}
              disabled={disabled}
              css={durationInputStyle}
              aria-label='Duration in minutes'
            />
            <span css={durationAddonStyle}>{t('common:minutes', {})}</span>
          </div>
          {durationError && <span css={errorStyle}>{durationError}</span>}
        </div>
      </div>
      {/* Only the start error sits under the whole row: date and time share one
          instant, so an API rejection of `starts_at` is not attributable to
          just one of them. Duration owns its own slot, under its own field. */}
      {startError && <span css={errorStyle}>{startError}</span>}
    </div>
  )
}

export default GameDateTimeFields

// ─── Styles ────────────────────────────────────────────────────────────────

const groupStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const errorStyle = css`
  ${paragraphXSmall}
  color: ${colors.ERROR_LIGHT};
`

const rowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;

  @media (max-width: 640px) {
    flex-direction: column;
  }
`

const fieldBase = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  @media (max-width: 640px) {
    width: 100%;
  }
`

const dateFieldStyle = css`
  ${fieldBase}
  flex: 1 1 0;
`

const timeFieldStyle = css`
  ${fieldBase}
  flex: 1 1 0;
`

const durationFieldStyle = css`
  ${fieldBase}
  flex: 1 1 0;
`

const labelStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  color: ${colors.SOLITUDE};
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 20px;
`

const splitLabelStyle = css`
  justify-content: space-between;
  gap: 8px;
`

const requiredMarkStyle = css`
  color: ${colors.WHITE};
`

const tbdStyle = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const tbdTextStyle = css`
  ${weightSemiBold}
  font-size: 11px;
  line-height: 16px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_50};
`

const tbdTextOnStyle = css`
  color: ${colors.WHITE};
`

// Matches the shared DatePicker chrome so these sit in the form exactly like
// every other 40px control.
const controlStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  height: 40px !important;
  width: 100%;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_200};
`

const iconStyle = css`
  color: ${colors.WHITE};
  font-size: 16px;
`

const durationWrapperStyle = css`
  display: flex;
  align-items: center;
  height: 40px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_200};
  overflow: hidden;
`

const durationErrorStyle = css`
  border-color: ${colors.ERROR_LIGHT};
`

const durationDisabledStyle = css`
  opacity: 0.45;
`

const durationInputStyle = css`
  &::placeholder {
    color: ${colors.NEUTRAL_100};
  }

  ${paragraphSmall}
  ${weightRegular}
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0 8px 0 12px;
  border: none;
  outline: none;
  background: transparent;
  color: ${colors.WHITE};

  /* The addon already says "Min"; the spinners only add noise at this width. */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
  appearance: textfield;
  -moz-appearance: textfield;
`

const durationAddonStyle = css`
  ${paragraphSmall}
  ${weightRegular}
  align-self: stretch;
  display: flex;
  align-items: center;
  padding: 0 10px;
  border-left: 1px solid ${colors.SECONDARY_100};
  color: ${colors.NEUTRAL_50};
  white-space: nowrap;
`

// DEFAULT_FLAIR is the same token the theme feeds antd as `colorPrimary`, so
// the selected time cell matches the selected day in the calendar.
const timePopupStyles = css`
  .${TIME_POPUP_CLASS}
    .ant-picker-time-panel-column
    > li.ant-picker-time-panel-cell-selected
    .ant-picker-time-panel-cell-inner {
    background: ${colors.DEFAULT_FLAIR};
    color: ${colors.WHITE};

    &:hover {
      background: ${colors.DEFAULT_FLAIR};
      color: ${colors.WHITE};
    }
  }
`
